import React from 'react';
import { Alert } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import { 
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

// Generic Message Alert Component
const MessageAlert = ({ 
  type = 'error', // 'error', 'success', 'warning', 'info'
  message,
  show = true,
  onClose = null,
  dismissible = false,
  className = '',
  style = {},
  icon = null, // Custom icon component
  iconColor = null, // Custom icon color
  backgroundColor = null, // Custom background color
  borderColor = null, // Custom border color
  textColor = null, // Custom text color
  shadow = true, // Enable/disable shadow
  rounded = true, // Enable/disable border radius
  padding = '12px 16px', // Custom padding
  margin = '0 0 16px 0', // Custom margin
  fontSize = '14px', // Custom font size
  fontFamily = 'Urbanist, Arial, sans-serif', // Custom font family
  lineHeight = '1.4', // Custom line height
  gap = '12px', // Gap between icon and text
  iconSize = '20px', // Icon size
  showIcon = true, // Show/hide icon
  children // Alternative to message prop
}) => {
  const { mode } = useThemeMode();
  
  if (!show || (!message && !children)) return null;

  // Default configurations based on type and theme
  const getDefaultConfig = () => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: mode === 'dark' ? '#071a19' : '#e8f5e8',
          borderColor: '#26C3C0',
          textColor: mode === 'dark' ? '#ffffff' : '#1a4d4a',
          iconColor: '#26C3C0',
          defaultIcon: CheckCircleIcon
        };
      case 'warning':
        return {
          backgroundColor: mode === 'dark' ? '#1a0f00' : '#fff8e1',
          borderColor: '#ffa726',
          textColor: mode === 'dark' ? '#ffffff' : '#e65100',
          iconColor: '#ffa726',
          defaultIcon: WarningIcon
        };
      case 'info':
        return {
          backgroundColor: mode === 'dark' ? '#001a1a' : '#e0f7fa',
          borderColor: '#26C3C0',
          textColor: mode === 'dark' ? '#ffffff' : '#1a4d4a',
          iconColor: '#26C3C0',
          defaultIcon: InfoIcon
        };
      case 'error':
      default:
        return {
          backgroundColor: mode === 'dark' ? '#23090b' : '#ffebee',
          borderColor: '#ef5350',
          textColor: mode === 'dark' ? '#ffffff' : '#d32f2f',
          iconColor: '#ef5350',
          defaultIcon: ErrorIcon
        };
    }
  };

  const config = getDefaultConfig();
  const IconComponent = icon || config.defaultIcon;

  // Use custom values if provided, otherwise use defaults
  const finalBackgroundColor = backgroundColor || config.backgroundColor;
  const finalBorderColor = borderColor || config.borderColor;
  const finalTextColor = textColor || config.textColor;
  const finalIconColor = iconColor || config.iconColor;

  const alertStyle = {
    backgroundColor: finalBackgroundColor,
    border: `1px solid ${finalBorderColor}`,
    borderRadius: rounded ? '8px' : '0',
    padding,
    margin,
    display: 'flex',
    alignItems: 'center',
    gap,
    ...(shadow && {
      boxShadow: `0 2px 8px rgba(${type === 'success' ? '76, 175, 80' : type === 'warning' ? '255, 152, 0' : type === 'info' ? '38, 195, 192' : '255, 68, 68'}, 0.2)`
    }),
    ...style
  };

  const textStyle = {
    color: finalTextColor,
    fontSize,
    fontFamily,
    lineHeight,
    margin: 0,
    flex: 1
  };

  const iconStyle = {
    color: finalIconColor,
    fontSize: iconSize,
    flexShrink: 0
  };

  // If dismissible is true and onClose is provided, use React Bootstrap Alert
  if (dismissible && onClose) {
    return (
      <Alert 
        variant={type === 'error' ? 'danger' : type} 
        onClose={onClose} 
        dismissible 
        className={className}
        style={alertStyle}
      >
        {showIcon && <IconComponent style={iconStyle} />}
        <span style={textStyle}>
          {message || children}
        </span>
      </Alert>
    );
  }

  // Otherwise, use custom div for more control
  return (
    <div style={alertStyle} className={className}>
      {showIcon && <IconComponent style={iconStyle} />}
      <span style={textStyle}>
        {message || children}
      </span>
    </div>
  );
};

export default MessageAlert; 